/**
 * A button that is a link: a notice's or a tip's button, to wherever the
 * owner pointed it in the Grow settings panel.
 *
 * Drawn as a real link, so it behaves like one (opened in a new tab, copied,
 * read out as a link): a page in the app through the router, a web page in a
 * new tab with nothing passed to it, an email address or a phone number as
 * it is. The link is read first (safeLink's readLink); one that was refused
 * gets no button at all.
 *
 * Used by: Today's notices (TodayNotice) and the tips (TipViews).
 *
 * @module LinkButton
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Button, type ButtonProps } from '@/shared/components/ui/button';
import type { SafeLink } from '@/shared/lib/safeLink';

/** Where it goes, what it says, and the button's looks. */
interface LinkButtonProps extends Pick<ButtonProps, 'size' | 'variant' | 'className'> {
  /** The link, read by readLink. */
  link: SafeLink;
  /** What it says. */
  children: React.ReactNode;
  /** Run as it is followed (a tip marked done), before the link opens. */
  onClick?: () => void;
}

/** A button-styled link, to a page in the app, a web page, or an address or number. */
export const LinkButton: React.FC<LinkButtonProps> = ({ link, children, onClick, ...looks }) => (
  <Button asChild {...looks}>
    {link.kind === 'app' ? (
      <Link to={link.to} onClick={onClick}>{children}</Link>
    ) : link.kind === 'web' ? (
      <a href={link.href} target="_blank" rel="noopener noreferrer" onClick={onClick}>{children}</a>
    ) : (
      <a href={link.href} onClick={onClick}>{children}</a>
    )}
  </Button>
);
